/**
 * Toast notification store
 * Manages toast notifications with auto-dismiss
 * Supports success, error, info, and warning types
 */

import { writable } from 'svelte/store';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number; // milliseconds, 0 = no auto-dismiss
}

interface ToastStore {
  toasts: Toast[];
}

// Create the store
function createToastStore() {
  const { subscribe, update } = writable<ToastStore>({ toasts: [] });

  // Track timeout IDs to prevent memory leaks
  const timeouts = new Map<string, number>();

  return {
    subscribe,

    /**
     * Show a toast notification
     * @param type - Type of toast (success, error, info, warning)
     * @param message - Message to display
     * @param duration - Auto-dismiss duration in ms (default: 5000, 0 = no auto-dismiss)
     */
    show: (type: ToastType, message: string, duration: number = 5000) => {
      const id = crypto.randomUUID();
      const toast: Toast = { id, type, message, duration };

      // Add toast to store
      update((state) => ({
        toasts: [...state.toasts, toast]
      }));

      // Auto-dismiss after duration
      if (duration > 0) {
        const timeoutId = setTimeout(() => {
          toastStore.dismiss(id);
          timeouts.delete(id);
        }, duration) as unknown as number;
        timeouts.set(id, timeoutId);
      }

      return id;
    },

    /**
     * Show success toast
     */
    success: (message: string, duration?: number) => {
      return toastStore.show('success', message, duration);
    },

    /**
     * Show error toast
     */
    error: (message: string, duration?: number) => {
      return toastStore.show('error', message, duration);
    },

    /**
     * Show info toast
     */
    info: (message: string, duration?: number) => {
      return toastStore.show('info', message, duration);
    },

    /**
     * Show warning toast
     */
    warning: (message: string, duration?: number) => {
      return toastStore.show('warning', message, duration);
    },

    /**
     * Dismiss a specific toast
     */
    dismiss: (id: string) => {
      // Clear timeout if it exists
      const timeoutId = timeouts.get(id);
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
        timeouts.delete(id);
      }

      update((state) => ({
        toasts: state.toasts.filter((t) => t.id !== id)
      }));
    },

    /**
     * Clear all toasts
     */
    clear: () => {
      // Clear all timeouts
      timeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      timeouts.clear();

      update(() => ({ toasts: [] }));
    }
  };
}

export const toastStore = createToastStore();
